/**
 * Zod schemas for the admin sponsored-listing management endpoints
 * (API-1362, M1 — Revenue v0, Admin-Mediated).
 *
 * These validate the admin-initiated create flow that backs the DES-51
 * "New sponsored listing" form. M1 is EVENT-level sponsorship only — the
 * `SponsoredListing.locationId` (venue-level) path is deferred to M2
 * (`schema.prisma` annotates the column `// venue-level sponsorship (M2)`),
 * so no venue-target shape is accepted here.
 *
 * Kept in `@loci/validation` (not inline in the route) so the same validators
 * can be reused by the web admin client and future services, matching the
 * `admin/event` / `admin/venue` / `admin/user` convention.
 */
import { z } from 'zod';

/** Matches the Prisma `SponsoredTier` enum — keep in sync with schema.prisma. */
export const sponsoredTierSchema = z.enum(['STARTER', 'GROWTH', 'FEATURED_PLUS']);
export type SponsoredTierValue = z.infer<typeof sponsoredTierSchema>;

/**
 * Matches the Prisma `SponsoredListingStatus` enum — keep in sync with
 * schema.prisma. There is deliberately NO `PAST_DUE` value: the sibling
 * issue API-1604 owns that decision. Exported for the queue filter chips.
 */
export const sponsoredListingStatusSchema = z.enum([
  'PENDING_APPROVAL',
  'ACTIVE',
  'PAUSED',
  'REJECTED',
  'CANCELED',
]);
export type SponsoredListingStatusValue = z.infer<typeof sponsoredListingStatusSchema>;

/**
 * Advertiser fields the admin supplies when creating (or reusing) an
 * advertiser inline from the create form. `contactEmail` is the natural
 * dedup key — the create endpoint reuses an existing advertiser matched on
 * this email rather than inserting a duplicate.
 *
 * `website` and `logoUrl` accept an empty string (the form renders
 * always-present inputs) and normalize it to `undefined` so we never persist
 * `""` — a cleared logo field must reach the DB as `null`, never an empty
 * string (API-1612 honest-absence).
 */
export const adminSponsoredAdvertiserInputSchema = z
  .object({
    name: z.string().trim().min(1).max(200),
    contactEmail: z.string().trim().toLowerCase().email().max(320),
    website: z
      .string()
      .trim()
      .max(500)
      .url()
      .optional()
      .or(z.literal('').transform(() => undefined)),
    contactPhone: z.string().trim().max(50).optional(),
    // API-1612 — advertiser brand logo URL. Optional; an empty string
    // normalizes to `undefined` (persisted as `null`) so a cleared logo is
    // honest absence, never `""`.
    logoUrl: z
      .string()
      .trim()
      .max(1000)
      .url()
      .optional()
      .or(z.literal('').transform(() => undefined)),
  })
  .strict();
export type AdminSponsoredAdvertiserInput = z.infer<typeof adminSponsoredAdvertiserInputSchema>;

/**
 * Body for `POST /api/v1/admin/sponsored`.
 *
 * Advertiser resolution supports the DES-51 `Existing` / `Create new` toggle:
 *   - `advertiserId` present → reuse that advertiser directly (`Existing`).
 *   - `advertiser` object present → reuse by `contactEmail` if one exists,
 *     otherwise create (`Create new`).
 * Exactly one of the two MUST be provided.
 *
 * `eventId` is required — M1 is event-level only. `tier` sets the SLA + price.
 *
 * `categoryExclusivity` (API-1606) is the optional category slug the listing
 * claims exclusively ("Featured+ only, add-on, one category per metro at a
 * time"). Normalized trim + lowercase, then validated against the same
 * canonical slug shape the codebase uses elsewhere (`venueSlugSchema`:
 * lowercase letters, numbers, hyphens). Tier eligibility (Featured+ only)
 * and holder uniqueness are ROUTE-level checks (422 / 409), not schema
 * checks — the schema only guarantees a well-formed slug.
 */
export const adminCreateSponsoredListingSchema = z
  .object({
    advertiserId: z.string().trim().min(1).max(128).optional(),
    advertiser: adminSponsoredAdvertiserInputSchema.optional(),
    eventId: z.string().trim().min(1).max(128),
    tier: sponsoredTierSchema,
    categoryExclusivity: z
      .string()
      .trim()
      .toLowerCase()
      .min(1)
      .max(120)
      .regex(/^[a-z0-9-]+$/, 'categoryExclusivity must be a lowercase category slug')
      .optional(),
  })
  .strict()
  .refine((body) => Boolean(body.advertiserId) !== Boolean(body.advertiser), {
    message:
      'Provide exactly one of `advertiserId` (reuse existing) or `advertiser` (create/reuse by email).',
    path: ['advertiser'],
  });
export type AdminCreateSponsoredListingBody = z.infer<typeof adminCreateSponsoredListingSchema>;

/**
 * Body for `POST /api/v1/advertisers/me/sponsored-listings` (API-1627).
 *
 * Self-serve create mirrors the admin create gates (PREMIUM, exclusivity tier)
 * but the advertiser is always resolved from `User.advertiserId` — clients
 * never supply an advertiser id (anti-IDOR). Claim-gated event eligibility is
 * enforced at the route layer, not here.
 */
export const createSelfServeSponsoredListingSchema = z
  .object({
    eventId: z.string().trim().min(1).max(128),
    tier: sponsoredTierSchema,
    categoryExclusivity: z
      .string()
      .trim()
      .toLowerCase()
      .min(1)
      .max(120)
      .regex(/^[a-z0-9-]+$/, 'categoryExclusivity must be a lowercase category slug')
      .optional(),
  })
  .strict();
export type CreateSelfServeSponsoredListingBody = z.infer<
  typeof createSelfServeSponsoredListingSchema
>;

/**
 * Body for `POST /api/v1/admin/sponsored/:id/approve` (API-1363).
 *
 * `note` is an optional internal annotation recorded on the AuditEvent only —
 * `SponsoredListing` has no approval-note column, and the note is an admin
 * trail artifact, not advertiser-facing content.
 */
export const adminApproveSponsoredListingSchema = z
  .object({
    note: z.string().trim().min(1).max(1000).optional(),
  })
  .strict();
export type AdminApproveSponsoredListingBody = z.infer<typeof adminApproveSponsoredListingSchema>;

/**
 * Body for `POST /api/v1/admin/sponsored/:id/reject` (API-1363).
 *
 * `reason` is REQUIRED — it is persisted to `SponsoredListing.rejectedReason`
 * and relayed verbatim to the advertiser in the rejection email, so a blank
 * rejection is never allowed. The route maps a missing/blank `reason` to
 * `400 MISSING_REASON` (a dedicated code, not the generic INVALID_PAYLOAD).
 */
export const adminRejectSponsoredListingSchema = z
  .object({
    reason: z.string().trim().min(1).max(1000),
  })
  .strict();
export type AdminRejectSponsoredListingBody = z.infer<typeof adminRejectSponsoredListingSchema>;
